import React from "react";
import Typography from "@dtci/spellbook/Typography";
import Skeleton from "@dtci/spellbook/Skeleton";
import { ContentList, ContentListItem } from "@dtci/spellbook/ContentList";
import Icon from "@dtci/spellbook/Icon";
import { usePPTState } from "tap/store/ppt/usePPT";
import TPRTypeCellRenderer from "../PPTPromosGrid/cell-renderers/TPRTypeCellRenderer";
import { computeWindowState } from "../PPTPromosGrid/cell-renderers/WindowCountdownCellRenderer";
import styles from "./pptPromoSummary.module.scss";
import PPTBadgeList from "../PPTBadgeList";
import { PMX_RELEASE_URL } from "tap/config/constants";

// Long-form version of the "Window" column on the promos grid, intended for
// header summary copy. Returns null when both dates are missing so the caller
// can fall back to the default "N/A" rendering.
const promoTimelineLabel = (startDate, endDate) => {
  const result = computeWindowState(startDate, endDate);
  if (!result) return null;
  if (result.state === "future") {
    return `${result.days} day${result.days === 1 ? "" : "s"} to Promo Start`;
  }
  if (result.state === "inWindow") return "Promo window is open";
  return "Promo window has ended";
};

const MetadataItem = ({
  label,
  value,
  labelIcon,
  valueIcon,
  renderer: Renderer,
  editable = false,
  loading = false,
  href,
}) => (
  <ContentListItem
    label={label}
    className={editable ? styles.editable : undefined}
  >
    <Typography variant="T10" className={styles.metadataItemTitle}>
      {labelIcon && (
        <Icon
          iconType="fa"
          faPackage="fal"
          iconName={labelIcon}
          ariaHidden={true}
        />
      )}
      {label}
    </Typography>

    <Skeleton isLoading={loading}>
      {Renderer ? (
        <Renderer value={value} />
      ) : (
        <Typography variant="T20" className={styles.metadataItemValue}>
          {valueIcon && (
            <Icon
              iconType="fa"
              faPackage="fal"
              iconName={valueIcon}
              ariaHidden={true}
            />
          )}
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#4f46e5" }}
            >
              {value}
            </a>
          ) : (
            value || "N/A"
          )}
        </Typography>
      )}
    </Skeleton>
  </ContentListItem>
);

export const PPTPromoSummary = () => {
  const { promotionDetail, promotionDetailLoading } = usePPTState();

  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const formatDate = (d) => (d ? dateFormatter.format(d) : null);
  const clientCount = promotionDetail?.clients?.length ?? 0;


  return (
    <>
      <ContentList className={styles.metadataList}>
        <MetadataItem
          label="TPR Type"
          value={promotionDetail?.tprType}
          labelIcon="fa-lock"
          renderer={TPRTypeCellRenderer}
          loading={promotionDetailLoading}
        />
        <MetadataItem
          label="Promo Start"
          value={formatDate(promotionDetail?.startDate)}
          labelIcon="fa-lock"
          valueIcon="fa-calendar"
          loading={promotionDetailLoading}
        />
        <MetadataItem
          label="Promo End"
          value={formatDate(promotionDetail?.endDate)}
          labelIcon="fa-lock"
          valueIcon="fa-calendar"
          loading={promotionDetailLoading}
        />
        <MetadataItem
          label="Promo Timeline"
          labelIcon="fa-clock"
          value={promoTimelineLabel(
            promotionDetail?.startDate,
            promotionDetail?.endDate,
          )}
          loading={promotionDetailLoading}
        />
        <MetadataItem
          label="Created By"
          value={promotionDetail?.releaseOwner}
          labelIcon="fa-lock"
          valueIcon="fa-user"
          loading={promotionDetailLoading}
        />
        <MetadataItem
          label="PMX Release ID"
          value={promotionDetail?.releaseId}
          labelIcon="fa-lock"
          valueIcon="fa-tag"
          loading={promotionDetailLoading}
          href={
            promotionDetail?.salesforceId
              ? PMX_RELEASE_URL(promotionDetail.salesforceId)
              : undefined
          }
        />
        <MetadataItem
          label="Territory"
          value={promotionDetail?.territories}
          labelIcon="fa-lock"
          loading={promotionDetailLoading}
          renderer={PPTBadgeList}
        />
        <ContentListItem label="Clients">
          <Typography variant="T10" className={styles.metadataItemTitle}>
            <Icon iconType="fa" faPackage="fal" iconName="fa-lock" />
            Clients
          </Typography>
          <Skeleton isLoading={promotionDetailLoading}>
            {clientCount > 0 ? (
              <>
                <Typography variant="T20" className={styles.metadataItemValue}>
                  <Icon iconType="fa" faPackage="fal" iconName="fa-users" />
                  {clientCount === 1 ? "1 Client" : `${clientCount} Clients`}
                </Typography>
                <PPTBadgeList value={promotionDetail?.clients} />
              </>
            ) : (
              <Typography variant="T20" className={styles.metadataItemValue}>
                —
              </Typography>
            )}
          </Skeleton>
        </ContentListItem>
      </ContentList>
    </>
  );
};
